/* File : src/pages/GuestPages/TicketPage.js */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import QRCode from '../../components/common/QRCode';
import Button from '../../components/common/Button';
import { publicApi } from '../../services/api';
import { useEvent } from '../../contexts/EventContext';
import '../../components/guest/Ticket.css';

/**
 * Page d'affichage du ticket après inscription ou récupération
 * Structurée comme la page du formulaire d'enregistrement
 */
const TicketPage = () => {
  const { phone: phoneParam } = useParams();
  const navigate = useNavigate();
  const { eventDetails, eventId, loading: eventLoading, changeEvent, selectedCity } = useEvent();
  const [userData, setUserData] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Charger les données de l'utilisateur et du ticket
  useEffect(() => {
    const fetchUserData = async () => {
      if (!phoneParam) {
        navigate('/');
        return;
      }
      
      setLoading(true);
      
      try {
        // Obtenir les informations de l'utilisateur par téléphone
        const response = await publicApi.getUserByPhone(phoneParam);
        
        if (response.data.user && response.data.ticket) {
          // Adapter les données utilisateur si nécessaire
          const user = response.data.user;
          // Si l'utilisateur a un champ 'name' mais pas 'firstName'/'lastName'
          if (user.name && !user.firstName) {
            const nameParts = user.name.split(' ');
            user.firstName = nameParts[0] || '';
            user.lastName = nameParts.slice(1).join(' ') || '';
          }
          
          setUserData(user);
          setTicketData(response.data.ticket);
          
          // Si le ticket a un event_id, s'assurer que le contexte d'événement est synchronisé
          if (response.data.ticket.event_id && response.data.ticket.event_id !== eventId) {
            console.log('Synchronizing event context with ticket event ID:', response.data.ticket.event_id);
            localStorage.setItem('currentEventId', response.data.ticket.event_id.toString());
            changeEvent && changeEvent(response.data.ticket.event_id.toString());
          }
        } else {
          setError('Impossible de charger les informations du ticket.');
          setTimeout(() => {
            navigate('/retrieve-ticket');
          }, 3000);
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Une erreur est survenue lors du chargement du ticket.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [phoneParam, navigate, eventId, changeEvent]);
  
  const handleBack = () => {
    navigate('/event');
  };
  
  // Formater la date pour l'affichage
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };
  
  // Formater l'heure pour l'affichage
  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };
  
  // Formater le numéro de téléphone pour l'affichage
  const formatPhone = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Exemple: 650123456 => 650 12 34 56
    return phoneNumber.replace(/(\d{3})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4');
  };
  
  const handleShareOnWhatsApp = () => {
    // Récupérer les paramètres de suivi depuis l'URL ou le localStorage
    const source = new URLSearchParams(window.location.search).get('source') || 'direct';
    const invitation = new URLSearchParams(window.location.search).get('invitation') || 'guinness';
    
    // Construire l'URL de l'application avec les paramètres de suivi
    const userPhoneNumber = userData?.phone || userData?.phoneNumber || userData?.phone_number || '';
    const appUrl = `${window.location.origin}/brightbar/?source=whatsapp&invitation=${invitation}&referrer=${userPhoneNumber}`;
    
    // Construire le message de partage
    const eventName = eventDetails ? eventDetails.event_name : 'l\'événement Guinness Bright Bar';
    const eventDate = eventDetails ? formatDate(eventDetails.event_date) : 'prochainement';
    const city = eventDetails?.city || selectedCity || '';
    const shareText = encodeURIComponent(`Je participe à ${eventName} ${city ? `à ${city}` : ''} le ${eventDate} ! Inscrivez-vous aussi via ce lien : ${appUrl}`);
    
    // Ouvrir WhatsApp avec le message
    window.open(`https://wa.me/?text=${shareText}`, '_blank');
  };
  
  const handleShareOnFacebook = () => {
    // Récupérer les paramètres de suivi depuis l'URL ou le localStorage
    const invitation = new URLSearchParams(window.location.search).get('invitation') || 'guinness';
    
    // Construire l'URL de l'application avec les paramètres de suivi
    const userPhoneNumber = userData?.phone || userData?.phoneNumber || userData?.phone_number || '';
    const appUrl = encodeURIComponent(`${window.location.origin}/brightbar/?source=facebook&invitation=${invitation}&referrer=${userPhoneNumber}`);
    
    // Ouvrir la boîte de dialogue de partage Facebook
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${appUrl}`, '_blank', 'width=600,height=400');
  };
  
  const handleDownloadPrint = () => {
    window.print();
  };
  
  // Afficher un message d'erreur si nécessaire
  if (error) {
    return (
      <div className="ticket-container">
        <div className="ticket-error">
          <div className="ticket-error-icon">❌</div>
          <h2>Désolé</h2>
          <p>{error}</p>
          <p>Vous allez être redirigé...</p>
        </div>
      </div>
    );
  }
  
  // Afficher le chargement pendant la récupération des données
  if (loading || eventLoading || !userData || !ticketData) {
    return (
      <div className="ticket-container">
        <div className="ticket-loading">
          <div className="ticket-spinner"></div>
          <p>Chargement de votre pass...</p>
        </div>
      </div>
    );
  }
  
  // Obtenir le prénom et le nom
  const firstName = userData.firstName || (userData.first_name || '');
  const lastName = userData.lastName || (userData.last_name || '');
  const fullName = `${firstName} ${lastName}`;
  // Obtenir le téléphone
  const userPhoneNumber = userData.phone || userData.phoneNumber || userData.phone_number || '';
  
  return (
    <div className="ticket-container">
      <div className="back-arrow" onClick={handleBack}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 19L5 12L12 5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      
      <div className="ticket-header">
        <img src="https://guinnessevents.cm/brightbar/assets/images/guinness-logo.png" alt="Guinness Bright Bar" className="ticket-logo" />
        <h1>GUINNESS BRIGHT BAR</h1>
        {/* Affichage de la ville */}
        {(eventDetails?.city || selectedCity) && (
          <div className="ticket-city-badge">
            {eventDetails?.city || selectedCity}
          </div>
        )}
      </div>
      
      <div className="ticket-content-container">
        <div className="ticket-content">
          <div className="ticket-info-wrapper">
            {/* QR Code et informations sur la même ligne */}
            <div className="ticket-main-section">
              {/* QR Code à gauche */}
              <div className="ticket-qrcode-section">
                <QRCode
                  data={ticketData.qrData}
                  size={175}
                  logo="/assets/images/guinness-icon.png"
                  logoSize={40}
                />
                <div className="ticket-code">{ticketData.ticketCode}</div>
              </div>
              
              {/* Informations à droite */}
              <div className="ticket-info-section">
                <div className="ticket-info-row">
                  <div className="ticket-info-label">Nom complet</div>
                  <div className="ticket-info-value">{fullName}</div>
                </div>
                
                <div className="ticket-info-row">
                  <div className="ticket-info-label">Téléphone</div>
                  <div className="ticket-info-value">{formatPhone(userPhoneNumber)}</div>
                </div>
                
                {eventDetails && (
                  <>
                    <div className="ticket-info-row">
                      <div className="ticket-info-label">Lieu</div>
                      <div className="ticket-info-value">{eventDetails.location}</div>
                    </div>
                    
                    <div className="ticket-info-row">
                      <div className="ticket-info-label">Date</div>
                      <div className="ticket-info-value">{formatDate(eventDetails.event_date)}</div>
                    </div>
                    
                    <div className="ticket-info-row">
                      <div className="ticket-info-label">Heure</div>
                      <div className="ticket-info-value">{formatTime(eventDetails.event_date)}</div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Section partage */}
            <div className="ticket-share-section">
              <h3 className="ticket-sharing-title">PARTAGEZ L'ÉVÈNEMENT</h3>
              
              <div className="ticket-share-buttons">
                <Button
                  variant="guinness"
                  size="md"
                  fullWidth
                  onClick={handleShareOnWhatsApp}
                >
                  Partagez sur Whatsapp
                </Button>
                
                <Button
                  variant="guinness"
                  size="md"
                  fullWidth
                  onClick={handleShareOnFacebook}
                >
                  Partagez sur Facebook
                </Button>
                
                <Button
                  variant="outline"
                  size="md"
                  fullWidth
                  onClick={handleDownloadPrint}
                >
                  Télécharger / Imprimer
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="ticket-footer">
        <p>
          La consommation d'alcool est réservée aux personnes âgées de plus de 21 ans. 
          L'abus d'alcool est dangereux pour la santé, à consommer avec modération.
        </p>
      </div>
    </div>
  );
};

export default TicketPage;